import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

import {
  Alert,
  Badge,
  Button,
  Input,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from '../../components/ui';
import {
  useAvailableConnectors,
  useClearDataSource,
  useDataSourceConfig,
  useSetDataSource,
} from './api';
import type { AppletDataSourceConfig } from './types';

const DEFAULT_SCHEMA = 'judgmental_accruals';

function modeLabel(mode: AppletDataSourceConfig['mode'] | undefined) {
  if (mode === 'saved') return 'Saved connector';
  if (mode === 'env_fallback') return 'Environment fallback';
  return 'Unconfigured';
}

function modeVariant(mode: AppletDataSourceConfig['mode'] | undefined) {
  if (mode === 'saved') return 'success';
  if (mode === 'env_fallback') return 'warning';
  return 'secondary';
}

export default function ConfigPage() {
  const { data: config, isLoading: configLoading, isError: configErrored, error: configError } = useDataSourceConfig();
  const { data: connectors = [], isLoading: connectorLoading, isError: connectorsErrored, error: connectorsError } = useAvailableConnectors();
  const setDataSource = useSetDataSource();
  const clearDataSource = useClearDataSource();

  const [connectorId, setConnectorId] = useState('');
  const [schema, setSchema] = useState(DEFAULT_SCHEMA);
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    if (!config || bootstrapped) return;
    setConnectorId(config.connector_id ?? '');
    setSchema(config.redshift_schema ?? DEFAULT_SCHEMA);
    setBootstrapped(true);
  }, [bootstrapped, config]);

  const activeConnector = useMemo(
    () => connectors.find(connector => connector.connector_id === connectorId),
    [connectorId, connectors],
  );
  const isLoading = configLoading || connectorLoading;
  const currentMode = config?.mode;
  const canSave = connectorId.length > 0 && schema.trim().length > 0 && !setDataSource.isPending;

  function saveConfiguration() {
    if (!canSave) return;
    setDataSource.mutate({
      connector_id: connectorId,
      redshift_schema: schema.trim() || DEFAULT_SCHEMA,
    });
  }

  function clearConfiguration() {
    clearDataSource.mutate();
    setConnectorId('');
    setSchema(DEFAULT_SCHEMA);
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
        <Panel>
          <PanelBody className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
            <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400 dark:text-gray-500" />
            Loading Judgmental Accruals data source configuration.
          </PanelBody>
        </Panel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <section className="space-y-5">
          <Panel>
            <PanelHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <PanelTitle>Data Source Configuration</PanelTitle>
                <PanelDescription>
                  Select the saved Redshift connection object and schema used by the EC-02e workbook.
                </PanelDescription>
              </div>
              <Badge variant={modeVariant(currentMode)}>{modeLabel(currentMode)}</Badge>
            </PanelHeader>
            <PanelBody className="space-y-5">
              {(configErrored || connectorsErrored) && (
                <Alert variant="error" title="Configuration unavailable">
                  {(configError instanceof Error && configError.message)
                    || (connectorsError instanceof Error && connectorsError.message)
                    || 'Unable to load the applet data source configuration.'}
                </Alert>
              )}

              {connectors.length === 0 && (
                <Alert variant="warning" title="No Redshift connectors found">
                  Create an enabled Amazon Redshift connection in Data Connections before saving this applet configuration.
                </Alert>
              )}

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="judgmental-redshift-connector">
                    Redshift connector
                  </label>
                  <select
                    id="judgmental-redshift-connector"
                    name="judgmental-redshift-connector"
                    value={connectorId}
                    onChange={event => setConnectorId(event.target.value)}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal focus:border-eliza-red/50 focus:outline-none focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
                  >
                    <option value="">Select a Redshift connector</option>
                    {connectors.map(connector => (
                      <option key={connector.connector_id} value={connector.connector_id}>
                        {connector.connector_name}
                      </option>
                    ))}
                  </select>
                  {config?.connector_name && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Current connector: {config.connector_name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="judgmental-redshift-schema">
                    Redshift schema
                  </label>
                  <Input
                    id="judgmental-redshift-schema"
                    name="judgmental-redshift-schema"
                    value={schema}
                    onChange={event => setSchema(event.target.value)}
                    placeholder={DEFAULT_SCHEMA}
                  />
                  {config?.redshift_schema && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Current schema: {config.redshift_schema}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="brand"
                  onClick={saveConfiguration}
                  disabled={!canSave}
                >
                  {setDataSource.isPending ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                  Save Configuration
                </Button>

                {config?.mode === 'saved' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearConfiguration}
                    disabled={clearDataSource.isPending}
                  >
                    Revert to Environment Fallback
                  </Button>
                )}

                {setDataSource.isSuccess && (
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    Saved.
                  </span>
                )}
                {setDataSource.isError && (
                  <span className="text-sm font-medium text-rose-700 dark:text-rose-300">
                    {setDataSource.error.message || 'Save failed.'}
                  </span>
                )}
                {clearDataSource.isSuccess && (
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    Cleared saved connector.
                  </span>
                )}
              </div>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Expected Redshift Shape</PanelTitle>
              <PanelDescription>
                The selected schema must contain the EC-02e source tables with tenant-filtered rows.
              </PanelDescription>
            </PanelHeader>
            <PanelBody>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  'ec02e_legal_matters',
                  'ec02e_unusual_items',
                  'ec02e_approval_gates',
                  'ec02e_journal_entries',
                  'ec02e_journal_lines',
                  'ec02e_impact_lines',
                  'ec02e_source_tabs',
                ].map(table => (
                  <div
                    key={table}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300"
                  >
                    {table}
                  </div>
                ))}
              </div>
            </PanelBody>
          </Panel>
        </section>

        <aside className="space-y-5">
          <Panel>
            <PanelHeader>
              <PanelTitle>Active Connection</PanelTitle>
            </PanelHeader>
            <PanelBody className="space-y-4">
              <ConnectionFact
                icon={ServerStackIcon}
                label="Connector"
                value={config?.connector_name || activeConnector?.connector_name || 'Not selected'}
              />
              <ConnectionFact
                icon={CircleStackIcon}
                label="Schema"
                value={config?.redshift_schema || schema || DEFAULT_SCHEMA}
              />
              <ConnectionFact label="Connector ID" value={config?.connector_id || connectorId || '-'} mono />
              <ConnectionFact label="Host" value={config?.host || '-'} mono />
            </PanelBody>
          </Panel>

          <Alert variant="neutral" title="Save behavior">
            Saving validates the Redshift connector and schema on the backend before persisting the applet configuration.
          </Alert>
        </aside>
      </div>
    </div>
  );
}

function ConnectionFact({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
        {Icon && <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
        {label}
      </div>
      <div className={mono ? 'break-all font-mono text-xs text-charcoal dark:text-gray-100' : 'text-sm font-medium text-charcoal dark:text-gray-100'}>
        {value}
      </div>
    </div>
  );
}
